import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBase } from '@mui/material';

import {
  TaDashboardFacilityCountResponse,
  useLaborLevelDesignations,
} from '@sbv-clients/data-access-api';
import {
  FacilitySelectField,
  LaborLevelLevelsFacilitySelectField,
} from '@sbv-clients/domain-ui-shared';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import { Alert, Box, Card, useTheme } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import { useTaDashboard } from '../../services/dashboard';
import { CardHead, CardTitle } from './components/card-head';
import { DashboardSkeleton } from './components/dashboard-skeleton';
import { LineIcon } from './components/line-icon';
import { useCardSx } from './components/use-card-sx';
import { ViewAllButton } from './components/view-all-button';
import { PageParamsProvider, usePageParamsContext } from './params-context';
import { RecentlyViewedCard } from './recently-viewed-card';
import { calcPct, getGreetingAndLabel, sumMinutesToHours } from './transform';

const BigNumber = ({ value, size = 32 }: { value: number; size?: number }) => {
  const theme = useTheme();
  return (
    <Box
      component="b"
      sx={{
        fontSize: `${size}px`,
        lineHeight: 1.1,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        color: theme.custom?.brandNavy,
      }}
    >
      {value}
    </Box>
  );
};

const PunchedInCard = ({ count }: { count: number }) => {
  const navigate = useNavigate();
  const cardSx = useCardSx();
  return (
    <Card
      sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: '10px' }}
      elevation={0}
    >
      <CardHead
        icon="punchedIn"
        label="Punched in"
        right={
          <ViewAllButton
            onClick={() =>
              navigate('/employees?isPunchedIn=true', {
                state: { skipPersistedFilters: true },
              })
            }
          />
        }
      />
      <Box sx={{ margin: 'auto 0' }}>
        <BigNumber value={count} size={40} />
      </Box>
    </Card>
  );
};

const PendingRequestsCard = ({
  missingPunchCount,
  timeChangeCount,
}: {
  missingPunchCount: number;
  timeChangeCount: number;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const cardSx = useCardSx();
  const pendingRequests = [
    {
      label: 'Missing punch',
      count: missingPunchCount,
      to: '/employees?hasPendingMissingPunchRequest=true',
    },
    {
      label: 'Time change',
      count: timeChangeCount,
      to: '/employees?hasPendingTimeChangeRequest=true',
    },
  ];
  const total = missingPunchCount + timeChangeCount;
  return (
    <Card
      sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}
      elevation={0}
    >
      <CardTitle
        title="Pending requests"
        right={
          <Box
            component="span"
            sx={{
              padding: '2px 9px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              background: theme.custom?.shellMenuItemCurrentBg,
              color: theme.custom?.brandIndigo,
            }}
          >
            {total} total
          </Box>
        }
      />
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          alignContent: 'space-evenly',
          margin: '6px -8px -6px',
        }}
      >
        {pendingRequests.map((row, index) => (
          <ButtonBase
            key={row.label}
            onClick={() =>
              navigate(row.to, { state: { skipPersistedFilters: true } })
            }
            sx={{
              justifyContent: 'flex-start',
              gap: '12px',
              padding: '10px 8px',
              borderRadius: '10px',
              textAlign: 'left',
              ...(index > 0 && {
                borderTop: `1px solid ${theme.custom?.tableRowLine}`,
                borderRadius: '0 0 10px 10px',
              }),
              '&:hover': { background: theme.custom?.hoverBg },
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                display: 'grid',
                placeItems: 'center',
                background: theme.custom?.shellMenuItemCurrentBg,
                color: theme.colors.primary.main,
              }}
            >
              <LineIcon name="request" />
            </Box>
            <Box
              component="b"
              sx={{
                flex: 1,
                fontSize: '14px',
                fontWeight: 600,
                color: theme.custom?.brandNavy,
              }}
            >
              {row.label}
            </Box>
            <Box
              component="b"
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: row.count
                  ? theme.colors.primary.main
                  : theme.custom?.tableHeaderText,
              }}
            >
              {row.count}
            </Box>
            <Box
              component="span"
              sx={{ display: 'flex', color: theme.custom?.iconButton }}
            >
              <LineIcon name="chevron" />
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Card>
  );
};

const StatCard = ({
  icon,
  label,
  value,
  prev,
  pct,
  up,
}: {
  icon: 'hours' | 'overtime';
  label: string;
  value: number;
  prev: number;
  pct: number;
  up: boolean;
}) => {
  const theme = useTheme();
  const cardSx = useCardSx();
  return (
    <Card
      sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: '10px' }}
      elevation={0}
    >
      <CardHead icon={icon} label={label} />
      <Box sx={{ display: 'grid', gap: '6px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <BigNumber value={value} />
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              padding: '1px 7px 1px 4px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              background: up
                ? theme.palette.success.light
                : theme.palette.error.light,
              color: up ? theme.palette.success.dark : theme.palette.error.main,
            }}
          >
            <LineIcon name={up ? 'up' : 'down'} size={14} />
            {pct}%
          </Box>
        </Box>
        <Box
          component="span"
          sx={{ fontSize: '13px', color: theme.custom?.headerInfoText }}
        >
          vs{' '}
          <Box
            component="b"
            sx={{ fontWeight: 600, color: theme.custom?.brandNavy }}
          >
            {prev}
          </Box>{' '}
          last period
        </Box>
      </Box>
    </Card>
  );
};

const ApproachingOTCard = ({
  approachingOt,
}: {
  approachingOt: TaDashboardFacilityCountResponse[];
}) => {
  const theme = useTheme();
  const { isMultiFacility } = useAuth();
  const navigate = useNavigate();
  const cardSx = useCardSx();
  const { values, setValues } = usePageParamsContext();

  const { data: laborLevelDesignations } = useLaborLevelDesignations();

  const count = useMemo(() => {
    const facilities = approachingOt.filter(
      (f) =>
        !isMultiFacility ||
        values.facilityId == null ||
        f.facilityId === values.facilityId
    );

    if (values.departmentId != null) {
      return facilities.reduce((acc, f) => {
        const dept = f.departments?.find(
          (d) => d.departmentLaborLevelId === values.departmentId
        );
        return acc + (dept?.employeeCount ?? 0);
      }, 0);
    }

    return facilities.reduce((acc, f) => acc + (f.employeeCount ?? 0), 0);
  }, [approachingOt, isMultiFacility, values.facilityId, values.departmentId]);
  return (
    <Card
      sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: '10px' }}
      elevation={0}
    >
      <CardHead
        icon="warn"
        tone="amber"
        label="Approaching OT"
        right={
          <ViewAllButton
            onClick={() => {
              const params = new URLSearchParams({ isApproachingOt: 'true' });
              if (
                values.departmentId != null &&
                laborLevelDesignations?.department?.level != null
              )
                params.set(
                  `laborLevel${laborLevelDesignations.department.level}`,
                  String(values.departmentId)
                );
              if (
                isMultiFacility &&
                values.facilityId != null &&
                laborLevelDesignations?.facility?.level != null
              )
                params.set(
                  `laborLevel${laborLevelDesignations.facility.level}`,
                  String(values.facilityId)
                );
              navigate(`/employees?${params}`, {
                state: { skipPersistedFilters: true },
              });
            }}
          />
        }
      />
      <Box sx={{ display: 'grid', alignItems: 'center', minHeight: 35 }}>
        <BigNumber value={count} />
      </Box>
      <Box
        sx={{
          mt: 'auto',
          display: 'grid',
          gap: '6px',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          '& label, & .MuiFormLabel-root': { display: 'none' },
          '& .MuiInputBase-root': {
            height: 32,
            borderRadius: '8px',
            fontSize: '13px',
            background: theme.custom?.hoverBg,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.custom?.tableRowLine} !important`,
          },
        }}
      >
        {isMultiFacility && (
          <FacilitySelectField
            fullWidth
            clearable
            density="compact"
            value={values.facilityId ?? ''}
            onChange={(e) =>
              setValues({
                facilityId: e.target.value as number,
                departmentId: null,
              })
            }
          />
        )}
        {!!laborLevelDesignations && (
          <LaborLevelLevelsFacilitySelectField
            fullWidth
            clearable
            density="compact"
            value={values.departmentId ?? ''}
            label={laborLevelDesignations?.department?.name ?? ''}
            level={laborLevelDesignations?.department?.level as number}
            onChange={(e) =>
              setValues({ departmentId: e.target.value as number })
            }
          />
        )}
      </Box>
    </Card>
  );
};

const FacilityDashboardContent = () => {
  const theme = useTheme();
  const { username } = useAuth();
  const { data, isLoading, isError, error } = useTaDashboard();
  const { greeting, todayLabel } = useMemo(() => getGreetingAndLabel(), []);

  const punchedInCount = data?.punchedInCount ?? 0;
  const missingPunchCount = data?.pendingMissingPunchEmployeeCount ?? 0;
  const timeChangeCount = data?.pendingTimeChangeEmployeeCount ?? 0;

  const hours = data?.hours ?? [];
  const totalHoursCurr = sumMinutesToHours(hours, 'totalMinutesCurrent');
  const totalHoursPrev = sumMinutesToHours(hours, 'totalMinutesPrevious');
  const otHoursCurr = sumMinutesToHours(hours, 'otMinutesCurrent');
  const otHoursPrev = sumMinutesToHours(hours, 'otMinutesPrevious');

  return (
    <Box
      sx={{
        minHeight: '100%',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          margin: '0 auto',
          padding: { xs: '16px 12px 32px', md: '26px 28px 40px' },
          display: 'grid',
          gap: '16px',
        }}
      >
        <Box component="header">
          <Box
            component="small"
            sx={{
              fontSize: '13px',
              color: theme.custom?.tableHeaderText,
            }}
          >
            {todayLabel}
          </Box>
          <Box
            component="h1"
            sx={{
              margin: '2px 0 0',
              fontSize: '24px',
              lineHeight: '32px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: theme.custom?.brandNavy,
            }}
          >
            {greeting}, {username}
          </Box>
        </Box>
        {isError ? (
          <Alert severity="error">{getApiErrorMessage(error)}</Alert>
        ) : isLoading ? (
          <DashboardSkeleton />
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
                gap: '16px',
              }}
            >
              <PunchedInCard count={punchedInCount} />
              <PendingRequestsCard
                missingPunchCount={missingPunchCount}
                timeChangeCount={timeChangeCount}
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: '16px',
              }}
            >
              <StatCard
                icon="hours"
                label="Total hours"
                value={totalHoursCurr}
                prev={totalHoursPrev}
                pct={calcPct(totalHoursCurr, totalHoursPrev)}
                up={totalHoursCurr >= totalHoursPrev}
              />
              <StatCard
                icon="overtime"
                label="Overtime hours"
                value={otHoursCurr}
                prev={otHoursPrev}
                pct={calcPct(otHoursCurr, otHoursPrev)}
                up={otHoursCurr >= otHoursPrev}
              />
              <ApproachingOTCard approachingOt={data?.approachingOt ?? []} />
            </Box>
            <RecentlyViewedCard />
          </>
        )}
      </Box>
    </Box>
  );
};

export const FacilityDashboardPage = () => (
  <PageParamsProvider>
    <FacilityDashboardContent />
  </PageParamsProvider>
);
